import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { TeacherFormValues } from "../types";
import {
  FaChalkboardTeacher,
  FaEnvelope,
  FaHome,
  FaPen,
  FaPhoneAlt,
  FaEllipsisV,
  FaUser,
  FaCalendarAlt,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import CopyButton from "../../../components/CopyButton";
import ErrorPage from "../../../components/ErrorPage";
import Spinner from "../../../components/Spinner";
import { useCan } from "../../../authz/useCan";
import {
  listTeacherClassrooms,
  updateTeacherContactSharing,
} from "../api";
import { loadTeacherDetails, upsertCachedTeacher } from "../cache";
import {
  ActionButton,
  BackButton,
  EmptyState,
  LdsPage,
  StatusBadge,
  cardEntryClass,
} from "../components/Lds";
import type {
  AppError,
  Teacher,
  TeacherAssignment,
} from "../types";
import { entityKey, httpStatus, teacherInitials, teacherName } from "../utils";
import { splitByTime, translateClassName } from "../../../utils/classroomUtils";

interface NormalizedAssignment {
  assignmentId?: string | number | null;
  classroomId?: string | number | null;
  className?: string | null;
  role?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  effectiveEnd?: string | null;
  subjectNames?: string[] | null;
}

const formatDateLabel = (
  value: string | null | undefined,
  fallback: string,
) => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString("nl-NL", { year: "numeric", month: "short" });
};

type TeacherDetailsTab = "details" | "classrooms" | "schedule";

const teacherDetailsTabFromQuery = (value: string | null): TeacherDetailsTab =>
  value === "classrooms" || value === "schedule" ? value : "details";

const DetailPanel = ({
  title,
  rows,
  action,
  children,
}: {
  title: string;
  rows?: Array<[string, string]>;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <section
    className={`${cardEntryClass} rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-sm dark:border-white/10 dark:bg-white/5`}
  >
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="text-sm font-semibold text-slate-800 dark:text-white">
        {title}
      </h2>
      {action}
    </div>
    {rows ? (
      <dl>
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-4 border-b border-slate-100 py-2.5 last:border-b-0 dark:border-white/10"
          >
            <dt className="text-sm text-slate-500 dark:text-slate-400">
              {label}
            </dt>
            <dd className="whitespace-pre-line text-sm font-medium text-slate-900 dark:text-white">
              {value}
            </dd>
          </div>
        ))}
      </dl>
    ) : null}
    {children}
  </section>
);

const ClassroomOverview = ({
  sections,
  returnTo,
  t,
}: {
  sections: Array<{
    id: string;
    title: string;
    data: NormalizedAssignment[];
    empty: string;
  }>;
  returnTo: string;
  t: TFunction;
}) => (
  <section
    className={`${cardEntryClass} rounded-[26px] border border-slate-200/80 bg-white/95 p-5 shadow-sm dark:border-white/10 dark:bg-white/5`}
  >
    <div className="mb-5 border-b border-slate-200 pb-4 dark:border-white/10">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
        {t("teachers.details.classroom_overview", {
          defaultValue: "Classroom overview",
        })}
      </h2>
    </div>
    <div className="grid gap-5 lg:grid-cols-3">
      {sections.map((section) => (
        <div
          key={section.id}
          className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-white/10 dark:bg-slate-950/40"
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold uppercase text-slate-600 dark:text-slate-300">
              {section.title}
            </h3>
            <StatusBadge tone="neutral">{section.data.length}</StatusBadge>
          </div>
          <div className="space-y-2">
            {section.data.length ? (
              section.data.map((assignment) => {
                const content = (
                  <>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {translateClassName(assignment.className ?? "")}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {formatDateLabel(
                        assignment.startDate,
                        t("teachers.details.date_unknown", {
                          defaultValue: "Date unknown",
                        }),
                      )}{" "}
                      -{" "}
                      {formatDateLabel(
                        assignment.endDate,
                        t("teachers.details.date_unknown", {
                          defaultValue: "Date unknown",
                        }),
                      )}
                    </p>
                    <StatusBadge tone="info">
                      {t(`classrooms.roles.${String(assignment.role || "PRIMARY").toLowerCase()}`, {
                        defaultValue: String(assignment.role || "PRIMARY")
                          .toLowerCase()
                          .replaceAll("_", " ")
                          .replace(/^./, (letter) => letter.toUpperCase()),
                      })}
                    </StatusBadge>
                    {assignment.subjectNames?.length ? (
                      <p className="mt-2 text-xs font-medium text-indigo-600 dark:text-indigo-300">{assignment.subjectNames.join(", ")}</p>
                    ) : null}
                  </>
                );
                return assignment.classroomId ? (
                  <Link
                    key={entityKey(
                      assignment.assignmentId ?? assignment.classroomId,
                    )}
                    to={`/school/classrooms/${assignment.classroomId}/details`}
                    state={{ returnTo }}
                    className="block rounded-xl border border-slate-200 bg-white p-3 text-sm shadow-sm transition hover:border-indigo-200 dark:border-white/10 dark:bg-white/5"
                  >
                    {content}
                  </Link>
                ) : (
                  <div
                    key={entityKey(
                      assignment.assignmentId ?? assignment.className,
                    )}
                    className="rounded-xl border border-slate-200 bg-white p-3 text-sm shadow-sm dark:border-white/10 dark:bg-white/5"
                  >
                    {content}
                  </div>
                );
              })
            ) : (
              <EmptyState>{section.empty}</EmptyState>
            )}
          </div>
        </div>
      ))}
    </div>
  </section>
);

const TeacherDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const canListTeachers = useCan("school.teachers", "list");
  const canReadTeachers = useCan("school.teachers", "read");
  const canCrudTeachers = useCan("school.teachers", "crud");
  const canManageTeachers = useCan("school.teachers", "manage");
  const canListClassrooms = useCan("school.classrooms", "list");
  const canReadClassrooms = useCan("school.classrooms", "read");
  const canViewClassrooms = useCan("school.classrooms", "view");
  const canCrudClassrooms = useCan("school.classrooms", "crud");
  const canManageClassrooms = useCan("school.classrooms", "manage");
  const canLoadClassrooms =
    canListClassrooms ||
    canReadClassrooms ||
    canViewClassrooms ||
    canCrudClassrooms ||
    canManageClassrooms;
  const canViewTeachers =
    canListTeachers || canReadTeachers || canCrudTeachers || canManageTeachers;
  const canEditTeachers = canCrudTeachers || canManageTeachers;

  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TeacherDetailsTab>(() =>
    teacherDetailsTabFromQuery(searchParams.get("tab")),
  );
  const [error, setError] = useState<AppError | null>(null);
  const [savingSharingKey, setSavingSharingKey] = useState<"sharePhone" | "shareEmail" | "shareAddress" | null>(null);
  const [contactError, setContactError] = useState<string | null>(null);

  useEffect(() => {
    setActiveTab(teacherDetailsTabFromQuery(searchParams.get("tab")));
  }, [searchParams]);

  const selectTab = (tab: TeacherDetailsTab) => {
    setActiveTab(tab);
    const nextSearchParams = new URLSearchParams(searchParams);
    if (tab === "details") nextSearchParams.delete("tab");
    else nextSearchParams.set("tab", tab);
    setSearchParams(nextSearchParams, { replace: true });
  };


  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!id || !canViewTeachers) {
        setError({
          message: t("error.403.message", {
            defaultValue: "You do not have permission.",
          }),
          status: 403,
        });
        setLoading(false);
        return;
      }

      try {
        const [teacherData, assignmentData] = await Promise.all([
          loadTeacherDetails(id),
          canLoadClassrooms ? listTeacherClassrooms(id) : Promise.resolve([]),
        ]);

        if (!cancelled) {
          setTeacher(teacherData);
          setAssignments(assignmentData);
        }
      } catch (err) {
        if (!cancelled) {
          setError({
            message:
              httpStatus(err) === 404
                ? t("teachers.details.not_found", {
                    defaultValue: "Teacher not found.",
                  })
                : httpStatus(err) === 403
                  ? t("error.403.message", {
                      defaultValue: "You do not have permission.",
                    })
                  : t("errors.fetch_teacher_details", {
                      defaultValue: "Failed to fetch teacher details.",
                    }),
            status: httpStatus(err),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [canLoadClassrooms, canViewTeachers, id, t]);

  const split = useMemo(
    () =>
      splitByTime(assignments) as {
        now: NormalizedAssignment[];
        future: NormalizedAssignment[];
        past: NormalizedAssignment[];
        unknown: NormalizedAssignment[];
      },
    [assignments],
  );

  if (loading) return <Spinner />;
  if (error) {
    return (
      <ErrorPage
        message={error.message}
        status={error.status}
        onRetry={() => window.location.reload()}
      />
    );
  }
  if (!teacher) return null;

  const sharing = [
    {
      key: "sharePhone" as const,
      label: t("family.common.phone", { defaultValue: "Phone" }),
      value: Boolean(teacher.sharePhone),
    },
    {
      key: "shareEmail" as const,
      label: t("family.common.email", { defaultValue: "Email" }),
      value: Boolean(teacher.shareEmail),
    },
    {
      key: "shareAddress" as const,
      label: t("family.common.address", { defaultValue: "Address" }),
      value: Boolean(teacher.shareAddress),
    },
  ];

  const sections = [
    {
      id: "now",
      title: t("teachers.details.current", { defaultValue: "Current" }),
      data: split.now,
      empty: t("teachers.details.none_current", {
        defaultValue: "No current classrooms",
      }),
    },
    {
      id: "future",
      title: t("teachers.details.future", { defaultValue: "Future" }),
      data: split.future,
      empty: t("teachers.details.none_future", {
        defaultValue: "No future classrooms",
      }),
    },
    {
      id: "past",
      title: t("teachers.details.past", { defaultValue: "Past" }),
      data: split.past,
      empty: t("teachers.details.none_past", {
        defaultValue: "No past classrooms",
      }),
    },
    ...(split.unknown.length ? [{
      id: "unknown",
      title: t("teachers.details.period_unknown", { defaultValue: "Period unknown" }),
      data: split.unknown,
      empty: "",
    }] : []),
  ];

  const teacherRecord = teacher as Teacher & Record<string, unknown>;
  const valueOf = (...keys: string[]) => {
    for (const key of keys) {
      const value = teacherRecord[key];
      if (value !== undefined && value !== null && String(value).trim() !== "")
        return String(value);
    }
    return "—";
  };
  const address = [teacher.address, teacher.zipCode, teacher.city]
    .filter(Boolean)
    .join(", ");
  const fullAddress = [
    teacher.address,
    [teacher.zipCode, teacher.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join("\n");
  const tabs = [
    {
      id: "details" as const,
      label: t("teachers.tabs.details", { defaultValue: "Details" }),
      icon: FaUser,
    },
    {
      id: "classrooms" as const,
      label: t("teachers.tabs.classrooms", { defaultValue: "Classrooms" }),
      icon: FaChalkboardTeacher,
    },
    {
      id: "schedule" as const,
      label: t("teachers.tabs.schedule", { defaultValue: "Schedule" }),
      icon: FaCalendarAlt,
    },
  ];

  const updateSharing = async (
    key: "sharePhone" | "shareEmail" | "shareAddress",
    value: boolean,
  ) => {
    if (!id || !canEditTeachers || savingSharingKey) return;

    const previousValue = Boolean(teacher[key]);
    if (previousValue === value) return;

    const optimisticTeacher: Teacher = { ...teacher, [key]: value };
    setTeacher(optimisticTeacher);
    upsertCachedTeacher(optimisticTeacher);
    setSavingSharingKey(key);
    setContactError(null);

    try {
      const payload: TeacherFormValues = {
        firstName: teacher.firstName ?? "",
        lastName: teacher.lastName ?? "",
        dateBirth: teacher.dateBirth ?? "",
        email: teacher.email ?? "",
        phone: teacher.phone ?? "",
        address: teacher.address ?? "",
        zipCode: teacher.zipCode ?? "",
        city: teacher.city ?? "",
        sharePhone: key === "sharePhone" ? value : Boolean(teacher.sharePhone),
        shareEmail: key === "shareEmail" ? value : Boolean(teacher.shareEmail),
        shareAddress: key === "shareAddress" ? value : Boolean(teacher.shareAddress),
        capabilities: teacher.capabilities ?? ["CLASS_TEACHER"],
        subjectIds: teacher.subjects?.map((subject) => String(subject.id)) ?? [],
      };
      const updated = await updateTeacherContactSharing(id, payload);
      const next: Teacher = { ...optimisticTeacher, ...(updated ?? {}) };
      setTeacher(next);
      upsertCachedTeacher(next);
    } catch (err) {
      const rolledBack: Teacher = { ...teacher, [key]: previousValue };
      setTeacher(rolledBack);
      upsertCachedTeacher(rolledBack);
      setContactError(
        httpStatus(err) === 403
          ? t("error.403.message", {
              defaultValue: "You do not have permission.",
            })
          : t("teachers.details.sharing_update_failed", {
              defaultValue: "Failed to update publication settings.",
            }),
      );
    } finally {
      setSavingSharingKey(null);
    }
  };

  return (
    <LdsPage>
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <BackButton fallback="/school/teachers" />

          <div className="flex items-center gap-2">
            {canEditTeachers ? (
              <ActionButton
                onClick={() => navigate(`/school/teachers/${id}/edit`)}
              >
                <span className="inline-flex items-center gap-2">
                  <FaPen aria-hidden="true" />
                  {t("teachers.common.edit", { defaultValue: "Edit" })}
                </span>
              </ActionButton>
            ) : null}
            <button
              type="button"
              aria-label={t("common.more_actions", {
                defaultValue: "More actions",
              })}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300"
            >
              <FaEllipsisV aria-hidden="true" />
            </button>
          </div>
        </div>

        <section
          className={`${cardEntryClass} relative overflow-hidden rounded-[28px] border border-violet-100 bg-gradient-to-r from-violet-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-violet-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7`}
        >
          <div className="relative z-10 flex min-w-0 items-center gap-5 pr-0 lg:pr-[34%]">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-violet-50 text-2xl font-bold text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-violet-950 dark:text-violet-100 sm:h-28 sm:w-28">
              {teacherInitials(teacher)}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                {t("teachers.details.title", {
                  defaultValue: "Teacher details",
                })}
              </p>
              <h1 className="mt-1 truncate text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                {teacherName(teacher)}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusBadge tone={teacher.keycloakUserId ? "success" : "warn"}>
                  {teacher.keycloakUserId
                    ? t("teachers.edit.linked_badge", {
                        defaultValue: "Linked",
                      })
                    : t("teachers.edit.not_linked_badge", {
                        defaultValue: "Not linked",
                      })}
                </StatusBadge>
                <StatusBadge tone="info">
                  <span className="inline-flex items-center gap-1.5">
                    <FaChalkboardTeacher aria-hidden="true" />
                    {t("teachers.details.teacher_role", {
                      defaultValue: "Teacher",
                    })}
                  </span>
                </StatusBadge>
                {teacher.capabilities?.map((capability) => (
                  <StatusBadge key={capability} tone="neutral">
                    {t(`teachers.capabilities.${capability.toLowerCase()}`, { defaultValue: capability.replaceAll("_", " ") })}
                  </StatusBadge>
                ))}
                {teacher.subjects?.map((subject) => (
                  <StatusBadge key={String(subject.id)} tone="info">{subject.name}</StatusBadge>
                ))}
              </div>
            </div>
          </div>
          <div
            className="pointer-events-none absolute inset-y-0 right-0 hidden w-[34%] items-center justify-center lg:flex"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 360 180"
              className="h-[155px] w-[310px] opacity-75 dark:opacity-45"
            >
              <rect
                x="126"
                y="38"
                width="142"
                height="86"
                rx="10"
                fill="currentColor"
                className="text-violet-200 dark:text-violet-800"
              />
              <rect
                x="141"
                y="53"
                width="112"
                height="56"
                rx="5"
                fill="currentColor"
                className="text-white/75 dark:text-slate-900/70"
              />
              <path
                d="M150 95l24-22 18 16 25-29 29 35"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-violet-300 dark:text-violet-600"
              />
              <rect
                x="108"
                y="126"
                width="178"
                height="12"
                rx="6"
                fill="currentColor"
                className="text-violet-200 dark:text-violet-800"
              />
              <circle
                cx="75"
                cy="76"
                r="31"
                fill="currentColor"
                className="text-sky-200 dark:text-sky-800"
              />
              <path
                d="M75 55v22l16 9"
                fill="none"
                stroke="currentColor"
                strokeWidth="7"
                strokeLinecap="round"
                className="text-white dark:text-slate-950"
              />
              <rect
                x="62"
                y="109"
                width="26"
                height="31"
                rx="5"
                fill="currentColor"
                className="text-sky-200 dark:text-sky-800"
              />
            </svg>
          </div>
        </section>

        <section
          className={`${cardEntryClass} grid gap-4 sm:grid-cols-2 xl:grid-cols-4`}
        >
          {[
            [
              FaEnvelope,
              t("family.common.email", { defaultValue: "Email" }),
              teacher.email || "—",
              t("teachers.details.primary_email", {
                defaultValue: "Primary email",
              }),
              teacher.email,
              t("common.copy_email", { defaultValue: "Copy email address" }),
              teacher.email ? `mailto:${teacher.email}` : null,
              t("teachers.details.send_email", { defaultValue: "Send email" }),
            ],
            [
              FaPhoneAlt,
              t("family.common.phone", { defaultValue: "Phone" }),
              teacher.phone || "—",
              t("teachers.details.mobile_phone", { defaultValue: "Mobile" }),
              teacher.phone,
              t("common.copy_phone", { defaultValue: "Copy phone number" }),
              teacher.phone ? `tel:${teacher.phone.replace(/[^\d+]/g, "")}` : null,
              t("teachers.details.call", { defaultValue: "Call" }),
            ],
            [
              FaChalkboardTeacher,
              t("teachers.details.classrooms", { defaultValue: "Classrooms" }),
              String(assignments.length),
              t("teachers.details.assigned", { defaultValue: "Assigned" }),
            ],
            [
              FaHome,
              t("family.common.address", { defaultValue: "Address" }),
              address || "—",
              t("teachers.details.contact_address", {
                defaultValue: "Contact address",
              }),
              address,
              t("common.copy_address", { defaultValue: "Copy address" }),
            ],
          ].map(([Icon, label, value, caption, copyValue, copyLabel, href, hrefLabel], index) => {
            const IconComponent = Icon as React.ComponentType<{
              className?: string;
            }>;
            return (
              <div
                key={index}
                className="rounded-2xl border border-slate-200 bg-white/90 p-4 dark:border-white/10 dark:bg-white/5"
              >
                <div className="flex items-center gap-2 text-xs font-semibold uppercase text-violet-600 dark:text-violet-300">
                  <IconComponent />
                  {label as string}
                </div>
                <div className="mt-2 flex items-start gap-1">
                  <p className="min-w-0 flex-1 break-words whitespace-pre-line text-base font-semibold text-slate-950 dark:text-white">
                    {href ? (
                      <a
                        href={href as string}
                        className="hover:text-indigo-600 hover:underline dark:hover:text-indigo-300"
                      >
                        {value as string}
                      </a>
                    ) : (
                      (value as string)
                    )}
                  </p>
                  {href ? (
                    <a
                      href={href as string}
                      aria-label={hrefLabel as string}
                      title={hrefLabel as string}
                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:text-slate-500 dark:hover:bg-white/10 dark:hover:text-indigo-300"
                    >
                      <IconComponent />
                    </a>
                  ) : null}
                  <CopyButton
                    value={copyValue as string | null | undefined}
                    label={(copyLabel as string) || ""}
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {caption as string}
                </p>
              </div>
            );
          })}
        </section>

        <nav
          className="grid grid-cols-2 border-b border-slate-200 dark:border-white/10 sm:grid-cols-3"
          aria-label={t("teachers.details.navigation", {
            defaultValue: "Teacher sections",
          })}
        >
          {tabs.map(({ id: tabId, label, icon: Icon }) => (
            <button
              key={tabId}
              type="button"
              onClick={() => selectTab(tabId)}
              className={`flex items-center justify-center gap-2 border-b-2 px-3 py-4 text-sm font-semibold transition ${activeTab === tabId ? "border-violet-500 text-violet-700 dark:text-violet-300" : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white"}`}
            >
              <Icon aria-hidden="true" />
              {label}
            </button>
          ))}
        </nav>

        {activeTab === "details" ? (
          <div className="grid gap-5 lg:grid-cols-2">
            <DetailPanel
              title={t("teachers.details.personal_data", {
                defaultValue: "Personal details",
              })}
              rows={[
                [
                  t("teachers.fields.first_name", {
                    defaultValue: "First name",
                  }),
                  valueOf("firstName", "firstname"),
                ],
                [
                  t("teachers.fields.last_name", { defaultValue: "Last name" }),
                  valueOf("lastName", "lastname"),
                ],
                [
                  t("teachers.fields.birth_date", {
                    defaultValue: "Date of birth",
                  }),
                  valueOf("dateOfBirth", "birthDate"),
                ],
              ]}
            />
            <DetailPanel
              title={t("teachers.details.contact_data", {
                defaultValue: "Contact details",
              })}
              rows={undefined}
            >
              <dl>
                {[
                  {
                    key: "shareEmail" as const,
                    label: t("family.common.email", { defaultValue: "Email" }),
                    value: teacher.email || "—",
                  },
                  {
                    key: "sharePhone" as const,
                    label: t("family.common.phone", { defaultValue: "Phone" }),
                    value: teacher.phone || "—",
                  },
                  {
                    key: "shareAddress" as const,
                    label: t("family.common.address", { defaultValue: "Address" }),
                    value: fullAddress || "—",
                  },
                ].map((row) => {
                  const sharingItem = sharing.find((item) => item.key === row.key);
                  const isShared = Boolean(sharingItem?.value);
                  const disabled = !canEditTeachers || Boolean(savingSharingKey);
                  const statusLabel = isShared
                    ? t("teachers.details.shared", { defaultValue: "Shared" })
                    : t("teachers.details.not_shared", { defaultValue: "Not shared" });

                  return (
                    <div
                      key={row.key}
                      className="grid grid-cols-[minmax(0,0.75fr)_minmax(0,1.2fr)_auto] items-center gap-4 border-b border-slate-100 py-3 last:border-b-0 dark:border-white/10"
                    >
                      <dt className="text-sm text-slate-500 dark:text-slate-400">
                        {row.label}
                      </dt>
                      <dd className="whitespace-pre-line text-sm font-medium text-slate-900 dark:text-white">
                        {row.value}
                      </dd>
                      <dd className="relative flex justify-end">
                        <button
                          type="button"
                          onClick={() => void updateSharing(row.key, !isShared)}
                          disabled={disabled}
                          aria-label={`${row.label}: ${statusLabel}`}
                          aria-pressed={isShared}
                          className={`group relative inline-flex h-9 w-9 items-center justify-center rounded-full border transition disabled:cursor-not-allowed disabled:opacity-60 ${
                            isShared
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300"
                              : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:text-slate-400 dark:hover:bg-white/10"
                          }`}
                        >
                          {isShared ? (
                            <FaEye aria-hidden="true" />
                          ) : (
                            <FaEyeSlash aria-hidden="true" />
                          )}
                          <span
                            role="tooltip"
                            className="pointer-events-none absolute bottom-full right-0 z-20 mb-2 hidden whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs font-semibold text-white shadow-lg group-hover:block group-focus-visible:block dark:bg-white dark:text-slate-950"
                          >
                            {statusLabel}
                          </span>
                        </button>
                      </dd>
                    </div>
                  );
                })}
                {contactError ? (
                  <div className="pt-3 text-sm font-medium text-rose-600 dark:text-rose-300">
                    {contactError}
                  </div>
                ) : null}
              </dl>
            </DetailPanel>
          </div>
        ) : null}

        {activeTab === "classrooms" ? (
          <ClassroomOverview
            sections={sections}
            returnTo={`/school/teachers/${id}/details?tab=classrooms`}
            t={t}
          />
        ) : null}

        {activeTab === "schedule" ? (
          <section
            className={`${cardEntryClass} rounded-[26px] border border-slate-200/80 bg-white/95 p-6 shadow-sm dark:border-white/10 dark:bg-white/5`}
          >
            <EmptyState>
              {t("teachers.details.schedule_empty", {
                defaultValue: "No schedule information is available.",
              })}
            </EmptyState>
          </section>
        ) : null}

      </div>
    </LdsPage>
  );
};

export default TeacherDetails;
